import {
  Box,
  Flex,
  FormRadioSelection,
  FormTextField,
  type OptionSelectionProps,
  Text,
} from '@shared/components';
import { type ReactNode, useEffect, useMemo } from 'react';
import { type FieldPath, type FieldPathValue, useFormContext } from 'react-hook-form';

import { type ComputerFeatureCheckConfig } from '../../containers/exam-location-request-room-computer-info-form/constants';
import {
  type ExamLocationRequestRoomComputerInfoFormInputs,
  type ExamLocationRequestRoomComputerInfoOtherDetailPath,
} from '../../containers/exam-location-request-room-computer-info-form/schema';

interface ComputerInfoFeatureCheckRowProps {
  config: ComputerFeatureCheckConfig;
  isReadonly?: boolean;
}

export default function ComputerInfoFeatureCheckRow(
  props: Readonly<ComputerInfoFeatureCheckRowProps>,
) {
  const { config, isReadonly = false } = props;
  const { key, featureType, label, required, options, withOthers } = config;
  const fieldName = `${featureType}.${key}` as const;

  const { watch, setValue } = useFormContext<ExamLocationRequestRoomComputerInfoFormInputs>();

  const radioName = (
    withOthers ? `${fieldName}.value` : fieldName
  ) as FieldPath<ExamLocationRequestRoomComputerInfoFormInputs>;
  const selectedValue = watch(radioName) as FieldPathValue<
    ExamLocationRequestRoomComputerInfoFormInputs,
    typeof radioName
  >;

  const radioOptions = useMemo(() => {
    if (!withOthers) return options;
    return [
      ...options,
      {
        label: 'อื่น ๆ (โปรดระบุ)',
        value: 'other',
        endAdornment: (
          <Box sx={{ maxWidth: '320px', flex: '1 1 auto', minWidth: '240px' }}>
            <FormTextField
              name={`${fieldName}.otherDetail`}
              placeholder='ระบุรายละเอียดเพิ่มเติม'
              fullWidth
              disabled={isReadonly || selectedValue !== 'other'}
              required={!!required}
              sx={{ maxWidth: '380px' }}
            />
          </Box>
        ),
      } as OptionSelectionProps & { endAdornment?: ReactNode },
    ];
  }, [withOthers, options, fieldName, isReadonly, required, selectedValue]);

  useEffect(() => {
    if (withOthers && selectedValue !== 'other') {
      setValue(
        `${fieldName}.otherDetail` as ExamLocationRequestRoomComputerInfoOtherDetailPath,
        '',
      );
    }
  }, [withOthers, selectedValue, setValue, fieldName]);

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(1),
        justifyContent: 'space-between',
        [theme.breakpoints.down('desktop')]: {
          flexDirection: 'column',
          gap: theme.spacing(2),
        },
      })}
    >
      <Box sx={{ width: '100%' }}>
        <Text variant='body1Semibold'>
          {label}
          {required ? (
            <Text
              component='span'
              sx={(theme) => ({ marginLeft: theme.spacing(1) })}
              color='error.main'
            >
              *
            </Text>
          ) : null}
        </Text>
      </Box>
      <Flex
        sx={(theme) => ({
          flex: 1,
          flexDirection: 'row',
          alignItems: 'end',
          gap: theme.spacing(2),
          flexWrap: 'wrap',
        })}
      >
        <FormRadioSelection
          name={radioName}
          options={radioOptions}
          required={required}
          disabled={isReadonly}
        />
      </Flex>
    </Flex>
  );
}
